import { extractMolEvent } from '@moleculeone/draw';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuPortal,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
  Icon,
  Skeleton,
  keyframes,
  styled,
} from '@moleculeone/ui';
import { Mol } from '@retro-ui/api-contract';
import React, { useState } from 'react';

import { DrawingSpace, PeriodicTable } from './components';
import { CompoundEditorActions, MolEvent, Space, useKeyboard } from './hooks';
import { getContextMenuItems } from './utils/getContextMenuItems';

const Container = styled('div', {
  'height': '100%',
  'width': '100%',
  'flex': 1,
  'overflow': 'hidden',
  'backgroundColor': 'white',
  'boxShadow': 'hsl(206 22% 7% / 15%) 0px 5px 19px -5px, hsl(206 22% 7% / 15%) 0px 5px 10px -7px',
  'borderRadius': '$medium',
  'position': 'relative',
  'margin': '0 $4',
  '& .atom-bg': { fill: '$slate3' },
});

const UnvailablePreview = styled(Container, {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '$4',
  fontSize: '$4',
  height: '50rem',
  maxHeight: '100%',
  [`& ${Icon}`]: {
    color: '$orange9',
  },
});

const drawingSpaceChoicesOpen = keyframes({
  '0%': { opacity: 1, height: '100%', transform: 'translateY(0)' },
  '45%': {
    opacity: 0,
    height: '100%',
    transform: 'translateY(-1rem)',
  },
  '55%': { opacity: 0, height: '34%', transform: 'translateY(1rem)' },
  '100%': { opacity: 1, height: '34%', transform: 'translateY(0)' },
});
const DrawingSpaceContainer = styled('div', {
  'height': '100%',
  'width': '100%',
  'padding': '$5',
  'mixBlendMode': 'darken',
  '&[data-choices-open=true]': {
    animation: `${drawingSpaceChoicesOpen} .4s both`,
  },
});

type CompoundSpaceProps = {
  loading?: boolean;
  compound: Space;
  actions: CompoundEditorActions;
  isSmilesError: boolean;
};

export const CompoundSpace = (props: CompoundSpaceProps) => {
  const { loading, compound, actions, isSmilesError } = props;

  const [event, setEvent] = useState<MolEvent>({ empty: true });
  const [choicesOpen, setChoicesOpen] = useState(false);

  useKeyboard({
    onKeyDown: event => {
      if (event.key === 'Escape') {
        setChoicesOpen(false);
      }
    },
  });

  if (loading) {
    return (
      <Skeleton
        css={{
          height: '100%',
          flex: 1,
          backgroundColor: '$slate3',
          borderRadius: '$medium',
          margin: '0 $4',
        }}
      />
    );
  }

  if (isSmilesError) {
    return (
      <UnvailablePreview>
        <Icon icon="warning" size="large" />
        Editor unavailable - incorrect SMILES
      </UnvailablePreview>
    );
  }

  const onContextMenuOpen = event => {
    setEvent(extractMolEvent(event));
  };

  const onPickAtom = (mol: Mol) => {
    // Hack to close context menu: https://github.com/radix-ui/primitives/issues/1307
    document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }));

    actions.onPickElement(event, mol.atoms[0].element);
  };

  const menuItems = getContextMenuItems({
    event,
    compound,
    actions,
  });

  return (
    <ContextMenu>
      <Container>
        <ContextMenuTrigger asChild>
          <DrawingSpaceContainer data-choices-open={choicesOpen}>
            <DrawingSpace
              onContextMenuOpen={onContextMenuOpen}
              {...compound}
              // viewport={viewport}
            />
          </DrawingSpaceContainer>
        </ContextMenuTrigger>
      </Container>

      <ContextMenuPortal>
        <ContextMenuContent>
          {menuItems.map(({ groupLabel, items }, idx) => (
            <React.Fragment key={idx}>
              {groupLabel && items.length > 0 && <ContextMenuLabel>{groupLabel}</ContextMenuLabel>}
              {items.map(([action, label]) =>
                action === 'pick-element' ? (
                  <ContextMenuSub key={label}>
                    <ContextMenuSubTrigger>{label}</ContextMenuSubTrigger>
                    <ContextMenuSubContent sideOffset={6} css={{ padding: '$4' }}>
                      <PeriodicTable onPickAtom={onPickAtom} />
                    </ContextMenuSubContent>
                  </ContextMenuSub>
                ) : (
                  <ContextMenuItem
                    key={label}
                    onSelect={() => {
                      if (typeof action === 'function') {
                        action();
                      }
                    }}
                  >
                    {label}
                  </ContextMenuItem>
                )
              )}
            </React.Fragment>
          ))}
        </ContextMenuContent>
      </ContextMenuPortal>
    </ContextMenu>
  );
};
